import React, { useCallback, useState, useEffect } from 'react';
import FullSizeContainer from '../containers/full-size/full-size-container';
import AccountsTable from '../accounts-table/accounts-table';
import Loading from '../loading/loading';
import MessageBox from '../message-box/message-box';
import HintIcon from '../hint-icon/hint-icon';
import '../common/common.css';
import UsersTable from '../users-table/users-table';
import { useSearchParams } from 'react-router-dom';

function FacesAdmin() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    accountName: '',
    totalSeats: '',
    expirationDate: '',
    branding: '',
  });
  const [accountData, setAccountData] = React.useState({});
  // eslint-disable-next-line no-unused-vars
  const [refreshKey, setRefreshKey] = React.useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      const res = await fetch(
        process.env.REACT_APP_FACES_API_BASE + '/accounts',
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
          },
        }
      );

      const j = await res.json();
      setAccountData(j);
      if (searchParams.get("success")) {
        setMessage("Account Successfully Updated");
        setMessageType("success");
      }
      setLoading(false);
    })();
  }, []);

  const showMessage = useCallback((msg, type) => {
    setMessage(msg);
    setMessageType(type);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setLoading(true);
    const result = await fetch(
      process.env.REACT_APP_FACES_API_BASE + '/accounts',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('facesIDToken'),
        },
        body: JSON.stringify({
          admin_email: formData.email,
          account_name: formData.accountName,
          fleet_name: formData.stack,
          stack_name: formData.stack,
          total_seats: Number(formData.totalSeats),
          account_renewal_date: formData.expirationDate,
          timeout_check_interval: 300,
        }),
      }
    );

    if (!result.ok) {
      setLoading(false);
      if (result.status === 409) {
        setMessage('Account with that name already exists.');
        setMessageType('error');
        return;
      } else {
        setMessage(result.statusText);
        setMessageType('error');
        return;
      }
    }

    const data = await result.json();
    console.log('Response:', data);
    setLoading(false);
    setMessage('Account created successfully.');
    setMessageType('success');
  };

  if (loading)
    return (
      <FullSizeContainer>
        <Loading />
      </FullSizeContainer>
    );

  return (
    <FullSizeContainer title="FACES™ ADMINISTRATION">
      <MessageBox
        type={messageType}
        message={message}
        onClose={() => setMessage('')}
      />
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ alignSelf: 'flex-start', margin: '2rem 0' }}>
          CREATE ACCOUNT
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <label style={{ flex: 1 }}>
              <h3 className="flex-row-center">
                PRIMARY USER EMAIL:
                <HintIcon hint="The email address of the primary user for this account. This user will be the administrator by default." />
              </h3>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
            <label style={{ flex: 1 }}>
              <h3 className="flex-row-center">
                ACCOUNT NAME:
                <HintIcon hint="The name of the account being created." />
              </h3>
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
                required
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
          </div>
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <label style={{ flex: 1 }}>
              <h3 className="flex-row-center">
                TOTAL SEATS:
                <HintIcon hint="The total number of seats available for this account. Each seat corresponds to one concurrent use of the FACES™ application." />
              </h3>
              <input
                type="number"
                name="totalSeats"
                value={formData.totalSeats}
                onChange={handleChange}
                required
                min="1"
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
            <label style={{ flex: 1 }}>
              <h3 className="flex-row-center">
                EXPIRATION DATE:
                <HintIcon hint="The date when the account will expire. Users will not have access to this account after then." />
              </h3>
              <input
                type="date"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
          </div>
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <label
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <h3 className="flex-row-center">
                STACK:
                <HintIcon hint="The FACES™ stack determines the features and capabilities available in the FACES™ software to this account." />
              </h3>
              <select
                name="stack"
                value={formData.stack || ''}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  marginTop: 4,
                  marginBottom: 16,
                  height: '2.65rem',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  color: '#23284d',
                }}
              >
                <option value="" disabled>
                  Select a stack
                </option>
                <option value="FacesEDU">Education</option>
                <option value="FacesLE">Law Enforcement</option>
                <option value="FacesPro">Professional</option>
              </select>
            </label>
          </div>
          <button type="submit" className="button" style={{ marginBottom: 2 }}>
            <b>CREATE NEW ACCOUNT</b>
          </button>
        </form>
      </div>
      <div
        style={{
          width: '100%',
          height: '2px',
          background: '#23284d',
          margin: '1.5rem 0',
          borderRadius: 2,
        }}
      />
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          flexDirection: 'column', // Change to column to stack textarea and button
          alignItems: 'stretch', // Stretch children to full width
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ marginTop: '2rem', alignSelf: 'flex-start' }}>
          MANAGE ACCOUNTS
        </h2>
        <AccountsTable setMessage={showMessage} />
      </div>
      <div
        style={{
          width: '100%',
          height: '2px',
          background: '#23284d',
          margin: '1.5rem 0',
          borderRadius: 2,
        }}
      />
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          flexDirection: 'column', // Change to column to stack textarea and button
          alignItems: 'stretch', // Stretch children to full width
        }}
      >
        <h2 style={{ marginTop: '2rem', alignSelf: 'flex-start' }}>
          MANAGE USERS
        </h2>
        <UsersTable
          accountData={accountData}
          refreshKey={refreshKey}
          showAccountName={true}
        />
      </div>
    </FullSizeContainer>
  );
}

export default FacesAdmin;
